import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { formatCurrency } from "@/lib/money";
import { canCancelInvoice, canWriteInvoice } from "@/lib/permissions";
import { canShowInvoicePdf } from "@/lib/invoicePdf";
import { isInvoiceFinalized } from "@/lib/invoiceStatus";
import { AddInvoiceLineModal } from "@/components/invoices/AddInvoiceLineModal";
import { InvoiceBillingAddressFields } from "@/components/invoices/InvoiceBillingAddressFields";
import { InvoiceLinesEditor } from "@/components/invoices/InvoiceLinesEditor";
import { InvoicePaymentsSection } from "@/components/invoices/InvoicePaymentsSection";
import { InvoiceDocumentActions } from "@/components/invoices/InvoiceDocumentActions";
import { InvoiceTypeBadge } from "@/components/invoices/InvoiceTypeBadge";
import { canAddInvoiceLine } from "@/lib/invoiceLineEdit";
import {
  billingAddressToPayload,
  guestToBillingAddressForm,
  validateBillingAddressForm,
  type BillingAddressFormState,
} from "@/lib/invoiceBillingAddress";
import { Button } from "@/components/ui/Button";
import { StatusBadge } from "@/components/ui/StatusBadge";
import type { Booking, Discount, Guest, Invoice, InvoiceListResponse, InvoiceType } from "@/types/camping";

type ElectricityBillingMode =
  | "flat_per_night"
  | "per_kwh"
  | "flat_with_included_kwh";

interface TenantSettings {
  electricity_billing: {
    mode: ElectricityBillingMode;
    included_kwh_per_night: string | null;
  };
}

export interface BookingInvoicesPanelProps {
  booking: Booking;
  userGroups: string[];
  guestEmail?: string | null;
  onChanged?: () => void;
}

export function BookingInvoicesPanel({
  booking,
  userGroups,
  guestEmail,
  onChanged,
}: BookingInvoicesPanelProps) {
  const canInvoice = canWriteInvoice(userGroups);
  const canStorno = canCancelInvoice(userGroups);
  const showPanel =
    booking.status === "checked_in" || booking.status === "checked_out";

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [includeXrechnungById, setIncludeXrechnungById] = useState<Record<string, boolean>>({});
  const [stornoId, setStornoId] = useState<string | null>(null);
  const [stornoReason, setStornoReason] = useState("");
  const [billingMode, setBillingMode] = useState<ElectricityBillingMode>("flat_per_night");
  const [electricityKwh, setElectricityKwh] = useState(
    booking.electricity_kwh != null ? String(booking.electricity_kwh) : "",
  );
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [selectedDiscountId, setSelectedDiscountId] = useState("");
  const [addLineInvoice, setAddLineInvoice] = useState<Invoice | null>(null);
  const [billingAddress, setBillingAddress] = useState<BillingAddressFormState>({
    override: false,
    company_name: "",
    name: "",
    street: "",
    postal_code: "",
    city: "",
    country_code: "DE",
  });

  const needsKwhForInvoice =
    Boolean(booking.wants_electricity) &&
    (billingMode === "per_kwh" || billingMode === "flat_with_included_kwh");

  const load = useCallback(() => {
    if (!showPanel) return;
    setLoading(true);
    setError(null);
    api
      .get<InvoiceListResponse>(`/api/v1/invoices?booking_id=${booking.id}`)
      .then((res) => setInvoices(res.items))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [booking.id, showPanel]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api
      .get<TenantSettings>("/api/v1/settings")
      .then((s) => setBillingMode(s.electricity_billing.mode))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!canInvoice || !showPanel) return;
    api
      .get<Discount[]>("/api/v1/discounts")
      .then(setDiscounts)
      .catch(() => setDiscounts([]));
  }, [canInvoice, showPanel]);

  useEffect(() => {
    if (!showPanel) return;
    api
      .get<Guest>(`/api/v1/guests/${booking.guest_id}`)
      .then((guest) => setBillingAddress(guestToBillingAddressForm(guest)))
      .catch(() => undefined);
  }, [booking.guest_id, showPanel]);

  useEffect(() => {
    setElectricityKwh(
      booking.electricity_kwh != null ? String(booking.electricity_kwh) : "",
    );
  }, [booking.electricity_kwh]);

  if (!showPanel) return null;

  const active = invoices.filter((inv) => inv.status !== "cancelled");
  const hasDeposit = active.some((inv) => inv.invoice_type === "deposit");
  const paidDeposit = Number(booking.deposit_paid_amount ?? 0);
  const requestedDeposit = Number(booking.deposit_request_amount ?? 0);
  const hasPaymentRequest = paidDeposit > 0 || requestedDeposit > 0;
  const hasFinal = active.some((inv) => inv.invoice_type === "final");
  const hasFull = active.some((inv) => inv.invoice_type === "full");
  const issuedDeposit = active.some(
    (inv) => inv.invoice_type === "deposit" && isInvoiceFinalized(inv.status),
  );
  const hasDraftOfType = (type: InvoiceType) =>
    invoices.some((inv) => inv.status === "draft" && inv.invoice_type === type);

  const depositSettled = Boolean(booking.deposit_settled_at);
  const canCreateDeposit =
    canInvoice &&
    !depositSettled &&
    !hasDeposit &&
    !hasPaymentRequest &&
    !hasFinal &&
    !hasFull &&
    !hasDraftOfType("deposit");
  const canCreateFull =
    canInvoice && !depositSettled && !hasDeposit && !hasFinal && !hasFull && !hasDraftOfType("full");
  const canCreateFinal =
    canInvoice &&
    !depositSettled &&
    issuedDeposit &&
    !hasFinal &&
    !hasFull &&
    !hasDraftOfType("final");

  async function createDraft(invoiceType: InvoiceType) {
    const billingError = validateBillingAddressForm(billingAddress);
    if (billingError) {
      setError(billingError);
      return;
    }
    setAction(`create-${invoiceType}`);
    setError(null);
    try {
      const payload: {
        booking_id: string;
        invoice_type: InvoiceType;
        electricity_kwh?: string;
        discount_id?: string;
        billing_address: ReturnType<typeof billingAddressToPayload>;
      } = {
        booking_id: booking.id,
        invoice_type: invoiceType,
        billing_address: billingAddressToPayload(billingAddress),
      };
      if (needsKwhForInvoice) {
        payload.electricity_kwh = electricityKwh.trim();
      }
      if (
        selectedDiscountId &&
        (invoiceType === "full" || invoiceType === "final")
      ) {
        payload.discount_id = selectedDiscountId;
      }
      await api.post<Invoice>("/api/v1/invoices/from-booking", payload);
      load();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Rechnungsentwurf konnte nicht erstellt werden.");
    } finally {
      setAction(null);
    }
  }

  async function issueInvoice(invoiceId: string) {
    setAction(`issue-${invoiceId}`);
    setError(null);
    try {
      await api.post<Invoice>(`/api/v1/invoices/${invoiceId}/issue`, {});
      load();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Rechnung konnte nicht ausgestellt werden.");
    } finally {
      setAction(null);
    }
  }

  async function sendInvoiceEmail(invoiceId: string) {
    setAction(`email-${invoiceId}`);
    setError(null);
    try {
      await api.post(`/api/v1/invoices/${invoiceId}/send-email`, {
        include_xrechnung: Boolean(includeXrechnungById[invoiceId]),
      });
      load();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "E-Mail konnte nicht gesendet werden.");
    } finally {
      setAction(null);
    }
  }

  async function cancelInvoice(invoiceId: string) {
    const reason = stornoReason.trim();
    if (reason.length < 3) {
      setError("Stornogrund muss mindestens 3 Zeichen haben.");
      return;
    }
    setAction(`cancel-${invoiceId}`);
    setError(null);
    try {
      await api.post<Invoice>(`/api/v1/invoices/${invoiceId}/cancel`, { reason });
      setStornoId(null);
      setStornoReason("");
      load();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Storno fehlgeschlagen.");
    } finally {
      setAction(null);
    }
  }

  return (
    <section className="space-y-3 rounded-lg border border-separator bg-fill-tertiary px-4 py-3">
      <AddInvoiceLineModal
        open={!!addLineInvoice}
        invoice={addLineInvoice}
        onClose={() => setAddLineInvoice(null)}
        onAdded={() => {
          load();
          onChanged?.();
        }}
      />
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium text-label">Rechnungen</h3>
          <p className="mt-0.5 text-xs text-label-secondary">
            Rechnungen werden getrennt von der Buchung gespeichert. Zusatzpositionen aus dem
            Artikelkatalog sind jederzeit möglich; Storno ersetzt eine Löschung.
          </p>
          {booking.deposit_settlement_summary ? (
            <p className="mt-1 text-xs text-label-secondary">{booking.deposit_settlement_summary}</p>
          ) : hasPaymentRequest ? (
            <p className="mt-1 text-xs text-label-secondary">
              {paidDeposit > 0
                ? `Zahlungsaufforderung: ${formatCurrency(paidDeposit)} bereits gezahlt. Die Rechnung zur Anreise verrechnet den Betrag.`
                : `Zahlungsaufforderung über ${formatCurrency(requestedDeposit)} ist noch offen.`}
            </p>
          ) : null}
          {booking.deposit_refund_error ? (
            <p className="mt-1 text-xs text-status-red">{booking.deposit_refund_error}</p>
          ) : null}
        </div>
        {canCreateDeposit || canCreateFull || canCreateFinal ? (
          <div className="flex flex-wrap gap-2">
            {canCreateDeposit ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={action === "create-deposit"}
                disabled={!!action}
                onClick={() => void createDraft("deposit")}
              >
                Anzahlung erstellen
              </Button>
            ) : null}
            {canCreateFinal ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={action === "create-final"}
                disabled={!!action}
                onClick={() => void createDraft("final")}
              >
                Schlussrechnung erstellen
              </Button>
            ) : null}
            {canCreateFull ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={action === "create-full"}
                disabled={!!action}
                onClick={() => void createDraft("full")}
              >
                Vollrechnung erstellen
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {canInvoice && (canCreateFull || canCreateFinal) ? (
        <label className="flex max-w-md flex-col gap-1 text-sm">
          Rabatt (optional)
          <select
            className="eh-input"
            value={selectedDiscountId}
            onChange={(e) => setSelectedDiscountId(e.target.value)}
          >
            <option value="">Kein Rabatt</option>
            {discounts.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
                {d.discount_type === "fixed" ? ` (${d.value} EUR)` : ` (${d.value} %)`}
              </option>
            ))}
          </select>
          <span className="text-xs text-label-secondary">
            Gilt für Voll- und Schlussrechnung; wird als negative Position gebucht.
          </span>
        </label>
      ) : null}

      {canInvoice && (canCreateDeposit || canCreateFull || canCreateFinal) ? (
        <InvoiceBillingAddressFields
          value={billingAddress}
          onChange={setBillingAddress}
          disabled={!!action}
        />
      ) : null}

      {canInvoice &&
      (canCreateDeposit || canCreateFull || canCreateFinal) &&
      needsKwhForInvoice ? (
        <label className="flex max-w-xs flex-col gap-1 text-sm">
          Stromverbrauch (kWh)
          <input
            type="number"
            min="0"
            step="0.001"
            required
            className="eh-input"
            value={electricityKwh}
            onChange={(e) => setElectricityKwh(e.target.value)}
          />
          <span className="text-xs text-label-secondary">
            {billingMode === "flat_with_included_kwh"
              ? "Mehrverbrauch über die inkludierte Menge wird zusätzlich berechnet."
              : "Verbrauch wird zum kWh-Preis aus der Buchungszuordnung berechnet."}
          </span>
        </label>
      ) : null}

      {error ? <p className="text-sm text-status-red">{error}</p> : null}
      {loading ? (
        <p className="text-sm text-label-secondary">Lade Rechnungen…</p>
      ) : invoices.length === 0 ? (
        <p className="text-sm text-label-secondary">
          Noch keine Rechnung zu dieser Buchung. Nach dem Check-in können Sie einen
          Rechnungsentwurf anlegen.
        </p>
      ) : (
        <ul className="space-y-3">
          {invoices.map((inv) => (
            <li
              key={inv.id}
              className="rounded-md border border-separator bg-surface px-3 py-3 text-sm"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-mono text-xs text-label-secondary">{inv.invoice_number}</span>
                <div className="flex flex-wrap items-center gap-1.5">
                  <InvoiceTypeBadge invoiceType={inv.invoice_type} />
                  <StatusBadge status={inv.status} kind="invoice" />
                </div>
              </div>
              <p className="mt-1 tabular-nums text-label">
                {formatCurrency(inv.total_amount, inv.currency)}
                {inv.open_amount != null && isInvoiceFinalized(inv.status) ? (
                  <span className="ml-2 text-label-secondary">
                    (offen: {formatCurrency(inv.open_amount, inv.currency)})
                  </span>
                ) : null}
              </p>
              {inv.lines.length > 0 ? (
                <div className="mt-2">
                  <InvoiceLinesEditor
                    invoice={inv}
                    booking={booking}
                    disabled={!!action}
                    onChanged={() => {
                      load();
                      onChanged?.();
                    }}
                  />
                </div>
              ) : null}
              {inv.status === "cancelled" && inv.cancellation_reason ? (
                <p className="mt-2 text-xs text-status-red">
                  Stornogrund: {inv.cancellation_reason}
                </p>
              ) : null}

              {canShowInvoicePdf(inv.status) ? (
                <InvoiceDocumentActions
                  invoiceId={inv.id}
                  invoiceNumber={inv.invoice_number}
                  status={inv.status}
                  disabled={!!action}
                >
                  {canInvoice ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      loading={action === `email-${inv.id}`}
                      disabled={!!action || !guestEmail?.trim()}
                      title={
                        guestEmail?.trim()
                          ? "Rechnung als ZUGFeRD-PDF per E-Mail senden"
                          : "Gast hat keine E-Mail-Adresse"
                      }
                      onClick={() => void sendInvoiceEmail(inv.id)}
                    >
                      Per E-Mail senden
                    </Button>
                  ) : null}
                </InvoiceDocumentActions>
              ) : null}
              {canInvoice && isInvoiceFinalized(inv.status) && canShowInvoicePdf(inv.status) ? (
                <label className="mt-2 flex items-start gap-2 text-xs text-label-secondary">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={Boolean(includeXrechnungById[inv.id])}
                    onChange={(e) =>
                      setIncludeXrechnungById((prev) => ({
                        ...prev,
                        [inv.id]: e.target.checked,
                      }))
                    }
                    disabled={!!action}
                  />
                  <span>Zusätzlich X-Rechnung (XML) per E-Mail anhängen</span>
                </label>
              ) : null}

              {canInvoice && canAddInvoiceLine(inv) ? (
                <div className="mt-3">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={!!action}
                    onClick={() => setAddLineInvoice(inv)}
                  >
                    Position hinzufügen
                  </Button>
                </div>
              ) : null}

              {canInvoice && inv.status === "draft" ? (
                <div className="mt-3">
                  <Button
                    type="button"
                    size="sm"
                    loading={action === `issue-${inv.id}`}
                    disabled={!!action}
                    onClick={() => void issueInvoice(inv.id)}
                  >
                    Rechnung ausstellen
                  </Button>
                  <p className="mt-1 text-xs text-label-tertiary">
                    Nach dem Ausstellen können weiterhin Zusatzpositionen ergänzt werden.
                  </p>
                </div>
              ) : null}

              {canStorno && isInvoiceFinalized(inv.status) ? (
                <div className="mt-3">
                  {stornoId === inv.id ? (
                    <div className="space-y-2">
                      <label className="block text-xs text-label-secondary">
                        Stornogrund (Pflichtfeld)
                        <textarea
                          className="eh-input mt-1 min-h-[3rem] w-full resize-y text-sm"
                          value={stornoReason}
                          onChange={(e) => setStornoReason(e.target.value)}
                          rows={2}
                        />
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          loading={action === `cancel-${inv.id}`}
                          disabled={!!action}
                          onClick={() => void cancelInvoice(inv.id)}
                        >
                          Storno bestätigen
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={!!action}
                          onClick={() => {
                            setStornoId(null);
                            setStornoReason("");
                          }}
                        >
                          Abbrechen
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={!!action}
                      onClick={() => setStornoId(inv.id)}
                    >
                      Rechnung stornieren
                    </Button>
                  )}
                </div>
              ) : null}

              <InvoicePaymentsSection
                invoice={inv}
                canRecord={canInvoice}
                onPaymentRecorded={() => {
                  load();
                  onChanged?.();
                }}
              />

            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
